import { useNostr } from '@nostrify/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

import { SKILL_KIND, MCP_KIND, parseSkillEvent, type Skill, type SkillType } from '@/lib/skills';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';

interface UseSkillsOptions {
  type?: SkillType;
  author?: string;
  tags?: string[];
  search?: string;
  limit?: number;
}

export function useSkills(options: UseSkillsOptions = {}) {
  const { nostr } = useNostr();
  const { type, author, tags, search, limit = 50 } = options;

  return useQuery({
    queryKey: ['skills', type, author, tags, search, limit],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      const kinds = type === 'skill' ? [SKILL_KIND] : type === 'mcp' ? [MCP_KIND] : [SKILL_KIND, MCP_KIND];

      const filter: Record<string, unknown> = {
        kinds,
        limit,
      };

      if (author) {
        filter.authors = [author];
      }

      if (tags && tags.length > 0) {
        filter['#t'] = tags;
      }

      const events = await nostr.query([filter as Parameters<typeof nostr.query>[0][0]], { signal });

      let skills = events
        .map(parseSkillEvent)
        .filter((s): s is Skill => s !== null);

      // Fetch author profiles and filter out demo/test accounts
      if (skills.length > 0) {
        const uniquePubkeys = [...new Set(skills.map((s) => s.pubkey))];
        try {
          const profileEvents = await nostr.query(
            [{ kinds: [0], authors: uniquePubkeys }],
            { signal }
          );
          const demoNames = new Set<string>();
          for (const e of profileEvents) {
            try {
              const meta = JSON.parse(e.content);
              if (
                typeof meta.name === 'string' &&
                meta.name.toLowerCase().includes('skillforge demo')
              ) {
                demoNames.add(e.pubkey);
              }
            } catch {
              // ignore unparseable profiles
            }
          }
          if (demoNames.size > 0) {
            skills = skills.filter((s) => !demoNames.has(s.pubkey));
          }
        } catch {
          // ignore profile fetch failures — show all skills
        }
      }

      // Client-side search if provided
      if (search) {
        const searchLower = search.toLowerCase();
        skills = skills.filter(
          (s) =>
            s.name.toLowerCase().includes(searchLower) ||
            s.description.toLowerCase().includes(searchLower) ||
            s.tags.some((t) => t.toLowerCase().includes(searchLower))
        );
      }

      // Sort by created_at descending
      skills.sort((a, b) => b.createdAt - a.createdAt);

      return skills;
    },
  });
}

export function useSkill(identifier: string, pubkey?: string) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['skill', identifier, pubkey],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(3000)]);

      const filter: Record<string, unknown> = {
        kinds: [SKILL_KIND, MCP_KIND],
        '#d': [identifier],
        limit: 10,
      };

      if (pubkey) {
        filter.authors = [pubkey];
      }

      const events = await nostr.query([filter as Parameters<typeof nostr.query>[0][0]], { signal });

      // Find the most recent valid skill
      const skills = events
        .map(parseSkillEvent)
        .filter((s): s is Skill => s !== null)
        .sort((a, b) => b.createdAt - a.createdAt);

      return skills[0] || null;
    },
    enabled: !!identifier,
  });
}

export function useMySkills() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useQuery({
    queryKey: ['my-skills', user?.pubkey],
    queryFn: async (c) => {
      if (!user?.pubkey) return [] as Skill[];
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      const events = await nostr.query(
        [{ kinds: [SKILL_KIND, MCP_KIND], authors: [user.pubkey], limit: 100 }],
        { signal }
      );

      return events
        .map(parseSkillEvent)
        .filter((s): s is Skill => s !== null)
        .sort((a, b) => b.createdAt - a.createdAt);
    },
    enabled: !!user?.pubkey,
  });
}

/** Publish a NIP-09 deletion event for a skill */
export function useDeleteSkill() {
  const { mutateAsync: publish } = useNostrPublish();
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();

  return useMutation({
    mutationFn: async (skill: Skill) => {
      // NIP-09: kind 5 deletion, reference both event id and addressable coord
      await publish({
        kind: 5,
        content: 'Deleted by author',
        tags: [
          ['e', skill.id],
          ['a', `${skill.event.kind}:${skill.pubkey}:${skill.identifier}`],
        ],
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-skills', user?.pubkey] });
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
  });
}
